import { Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TimelineParkingEvent {
    id: string;
    plate?: string;
    startedAt: string | Date;
    endedAt?: string | Date | null;
    isOverflow?: boolean;
}

export interface OccupancyStepPoint {
    time: Date;
    timeMs: number;
    occupancy: number;
    activePlates: string[];
    overflowPlates: string[];
    coveredPlates: string[];
    isAtCapacity: boolean;
    isOverbooked: boolean;
    isPeak: boolean;
    hasOverflow: boolean;
    xPct: number;
    yPct: number;
}

export interface OverbookedPeriodIncident {
    startTime: Date;
    endTime: Date;
    isOngoing: boolean;
    durationMinutes: number;
    peakOccupancy: number;
    excessCars: number;
    xStartPct: number;
    xWidthPct: number;
    activePlates: string[];
    overflowPlates: string[];
    coveredPlates: string[];
}

export interface OverbookedVehicleSummary {
    plate: string;
    startedAt: Date;
    endedAt: Date | null;
    isCurrentlyActive: boolean;
    durationMinutes: number;
}

@Component({
    selector: 'jjames-timeline-chart',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './timeline-chart.component.html',
    styleUrl: './timeline-chart.component.scss'
})
export class JJamesTimelineChartComponent {
    /** Maximum allowed cars (capacity limit) */
    readonly capacity = input<number>(4);

    /** Parking events to plot */
    readonly events = input<TimelineParkingEvent[]>([]);

    /** Optional window range overrides */
    readonly windowStart = input<Date | string | undefined>(undefined);
    readonly windowEnd = input<Date | string | undefined>(undefined);

    /** Active hover tooltip point */
    readonly hoveredPoint = signal<OccupancyStepPoint | null>(null);
    readonly hoverX = signal<number | null>(null);

    /** View range preset mode: 'fit' (data-focused with margin), '24h', '7d', 'all' */
    readonly viewPreset = signal<'fit' | '24h' | '7d' | 'all'>('fit');

    /** Effective window bounds with smart earlier start time padding */
    readonly windowBounds = computed<{ start: number; end: number }>(() => {
        const customStart = this.windowStart();
        const customEnd = this.windowEnd();
        if (customStart && customEnd) {
            return {
                start: new Date(customStart).getTime(),
                end: new Date(customEnd).getTime()
            };
        }

        const evs = this.events();
        const preset = this.viewPreset();

        if (evs.length === 0) {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            const start = d.getTime();
            d.setHours(23, 59, 59, 999);
            const end = d.getTime();
            return { start, end };
        }

        const starts = evs.map(e => new Date(e.startedAt).getTime()).filter(t => !isNaN(t));
        const ends = evs.map(e => (e.endedAt ? new Date(e.endedAt).getTime() : null)).filter((t): t is number => t !== null && !isNaN(t));
        const hasActiveStays = evs.some(e => !e.endedAt);

        const minEventStart = Math.min(...starts);
        const maxEventEnd = hasActiveStays
            ? Math.max(Date.now(), ...ends, minEventStart + 60 * 60 * 1000)
            : (ends.length > 0 ? Math.max(...ends) : minEventStart + 60 * 60 * 1000);

        if (preset === '24h') {
            const end = hasActiveStays ? Date.now() : maxEventEnd;
            return { start: end - 24 * 3600 * 1000, end };
        }

        if (preset === '7d') {
            const end = hasActiveStays ? Date.now() : maxEventEnd;
            return { start: end - 7 * 24 * 3600 * 1000, end };
        }

        if (preset === 'all') {
            const end = Math.max(Date.now(), maxEventEnd);
            return { start: minEventStart - 3600 * 1000, end: end + 3600 * 1000 };
        }

        // 'fit' mode: Focused around the actual data with generous earlier start buffer
        const rawSpan = Math.max(maxEventEnd - minEventStart, 15 * 60 * 1000); // at least 15 mins

        // Buffer calculation: give at least 20-30 mins or 20% of data span before the earliest event
        let bufferBeforeMs: number;
        let bufferAfterMs: number;

        if (rawSpan <= 30 * 60 * 1000) {
            bufferBeforeMs = 20 * 60 * 1000;
            bufferAfterMs = 20 * 60 * 1000;
        } else if (rawSpan <= 3 * 3600 * 1000) {
            bufferBeforeMs = 30 * 60 * 1000;
            bufferAfterMs = 30 * 60 * 1000;
        } else if (rawSpan <= 24 * 3600 * 1000) {
            bufferBeforeMs = 90 * 60 * 1000;
            bufferAfterMs = 90 * 60 * 1000;
        } else {
            bufferBeforeMs = Math.max(2 * 3600 * 1000, rawSpan * 0.08);
            bufferAfterMs = Math.max(2 * 3600 * 1000, rawSpan * 0.08);
        }

        return {
            start: minEventStart - bufferBeforeMs,
            end: maxEventEnd + bufferAfterMs
        };
    });

    readonly effectiveStart = computed<number>(() => this.windowBounds().start);
    readonly effectiveEnd = computed<number>(() => this.windowBounds().end);

    /** Time span in milliseconds */
    readonly timeSpanMs = computed(() => {
        const span = this.effectiveEnd() - this.effectiveStart();
        return Math.max(span, 1000 * 60 * 15); // min 15 mins
    });

    /** Reconstructed chronological occupancy step points */
    readonly stepPoints = computed<OccupancyStepPoint[]>(() => {
        const evs = this.events();
        const startMs = this.effectiveStart();
        const endMs = this.effectiveEnd();
        const spanMs = this.timeSpanMs();
        const cap = this.capacity();

        if (evs.length === 0) {
            return [
                {
                    time: new Date(startMs),
                    timeMs: startMs,
                    occupancy: 0,
                    activePlates: [],
                    overflowPlates: [],
                    coveredPlates: [],
                    isAtCapacity: false,
                    isOverbooked: false,
                    isPeak: false,
                    hasOverflow: false,
                    xPct: 0,
                    yPct: 100
                },
                {
                    time: new Date(endMs),
                    timeMs: endMs,
                    occupancy: 0,
                    activePlates: [],
                    overflowPlates: [],
                    coveredPlates: [],
                    isAtCapacity: false,
                    isOverbooked: false,
                    isPeak: false,
                    hasOverflow: false,
                    xPct: 100,
                    yPct: 100
                }
            ];
        }

        // Discrete time events: +1 on entry, -1 on exit
        interface TimeTransition {
            timeMs: number;
            delta: number;
            plate?: string;
            isOverflow?: boolean;
            isEntry: boolean;
            event: TimelineParkingEvent;
        }

        const transitions: TimeTransition[] = [];

        for (const e of evs) {
            const entryTime = new Date(e.startedAt).getTime();
            if (!isNaN(entryTime)) {
                transitions.push({
                    timeMs: entryTime,
                    delta: 1,
                    plate: e.plate,
                    isOverflow: e.isOverflow,
                    isEntry: true,
                    event: e
                });
            }

            if (e.endedAt) {
                const exitTime = new Date(e.endedAt).getTime();
                if (!isNaN(exitTime)) {
                    transitions.push({
                        timeMs: exitTime,
                        delta: -1,
                        plate: e.plate,
                        isOverflow: e.isOverflow,
                        isEntry: false,
                        event: e
                    });
                }
            }
        }

        // Sort transitions chronologically
        transitions.sort((a, b) => a.timeMs - b.timeMs);

        // Helper to resolve active plate roles at any transition moment
        const activeStays = new Map<string, { plate: string; isOverflow?: boolean; enteredAt: number }>();

        const calculatePlatesBreakdown = () => {
            const list = Array.from(activeStays.values()).sort((a, b) => a.enteredAt - b.enteredAt);
            const activeSet = new Set<string>();
            const coveredSet = new Set<string>();
            const overflowSet = new Set<string>();

            for (const stay of list) {
                const p = stay.plate;
                activeSet.add(p);
                if (stay.isOverflow) {
                    overflowSet.add(p);
                    coveredSet.delete(p);
                } else if (coveredSet.size < cap && !overflowSet.has(p)) {
                    coveredSet.add(p);
                } else if (!coveredSet.has(p)) {
                    overflowSet.add(p);
                }
            }

            return {
                active: Array.from(activeSet),
                overflow: Array.from(overflowSet),
                covered: Array.from(coveredSet)
            };
        };

        const yMax = Math.max(cap + 1, 4);
        const points: OccupancyStepPoint[] = [];
        let currentOccupancy = 0;

        // Initial point at startMs
        points.push({
            time: new Date(startMs),
            timeMs: startMs,
            occupancy: 0,
            activePlates: [],
            overflowPlates: [],
            coveredPlates: [],
            isAtCapacity: false,
            isOverbooked: false,
            isPeak: false,
            hasOverflow: false,
            xPct: 0,
            yPct: 100
        });

        for (const t of transitions) {
            if (t.timeMs < startMs || t.timeMs > endMs) {
                // Keep activeStays map accurate even outside window
                if (t.isEntry && t.plate) {
                    activeStays.set(t.event.id || t.plate, { plate: t.plate, isOverflow: t.isOverflow, enteredAt: t.timeMs });
                } else if (!t.isEntry && t.plate) {
                    activeStays.delete(t.event.id || t.plate);
                }
                currentOccupancy = Math.max(0, currentOccupancy + t.delta);
                continue;
            }

            const xPct = Math.max(0, Math.min(100, ((t.timeMs - startMs) / spanMs) * 100));

            // Step curve: add previous level point right before transition
            if (points.length > 0) {
                const last = points[points.length - 1];
                if (last.timeMs !== t.timeMs) {
                    const prevPlates = calculatePlatesBreakdown();
                    points.push({
                        time: new Date(t.timeMs),
                        timeMs: t.timeMs,
                        occupancy: currentOccupancy,
                        activePlates: prevPlates.active,
                        overflowPlates: prevPlates.overflow,
                        coveredPlates: prevPlates.covered,
                        isAtCapacity: currentOccupancy === cap,
                        isOverbooked: currentOccupancy > cap,
                        isPeak: false,
                        hasOverflow: currentOccupancy > cap,
                        xPct,
                        yPct: Math.max(0, 100 - (currentOccupancy / yMax) * 100)
                    });
                }
            }

            // Apply transition
            currentOccupancy = Math.max(0, currentOccupancy + t.delta);
            if (t.plate) {
                if (t.isEntry) {
                    activeStays.set(t.event.id || t.plate, { plate: t.plate, isOverflow: t.isOverflow, enteredAt: t.timeMs });
                } else {
                    activeStays.delete(t.event.id || t.plate);
                }
            }

            const currentPlates = calculatePlatesBreakdown();
            points.push({
                time: new Date(t.timeMs),
                timeMs: t.timeMs,
                occupancy: currentOccupancy,
                activePlates: currentPlates.active,
                overflowPlates: currentPlates.overflow,
                coveredPlates: currentPlates.covered,
                isAtCapacity: currentOccupancy === cap,
                isOverbooked: currentOccupancy > cap,
                isPeak: false,
                hasOverflow: currentOccupancy > cap,
                xPct,
                yPct: Math.max(0, 100 - (currentOccupancy / yMax) * 100)
            });
        }

        // Final point at endMs
        if (points.length > 0 && points[points.length - 1].timeMs < endMs) {
            const finalPlates = calculatePlatesBreakdown();
            points.push({
                time: new Date(endMs),
                timeMs: endMs,
                occupancy: currentOccupancy,
                activePlates: finalPlates.active,
                overflowPlates: finalPlates.overflow,
                coveredPlates: finalPlates.covered,
                isAtCapacity: currentOccupancy === cap,
                isOverbooked: currentOccupancy > cap,
                isPeak: false,
                hasOverflow: currentOccupancy > cap,
                xPct: 100,
                yPct: Math.max(0, 100 - (currentOccupancy / yMax) * 100)
            });
        }

        // Calculate peak flag
        const peak = points.reduce((max, p) => Math.max(max, p.occupancy), 0);
        return points.map(p => ({
            ...p,
            isPeak: p.occupancy > 0 && p.occupancy === peak
        }));
    });

    /** Y-axis max scale */
    readonly yAxisMax = computed(() => {
        const cap = this.capacity();
        const pts = this.stepPoints();
        const maxOcc = pts.reduce((max, p) => Math.max(max, p.occupancy), 0);
        return Math.max(cap + 1, maxOcc + 1, 4);
    });

    /** SVG polyline points string for the step line */
    readonly linePathSvg = computed(() => {
        const pts = this.stepPoints();
        if (pts.length === 0) return '';
        return pts.map(p => `${(p.xPct * 8).toFixed(1)},${((p.yPct / 100) * 200 + 20).toFixed(1)}`).join(' ');
    });

    /** SVG polygon points string for the gradient area under line */
    readonly areaPathSvg = computed(() => {
        const pts = this.stepPoints();
        if (pts.length === 0) return '';
        const lineCoords = pts.map(p => `${(p.xPct * 8).toFixed(1)},${((p.yPct / 100) * 200 + 20).toFixed(1)}`).join(' ');
        const lastX = (pts[pts.length - 1].xPct * 8).toFixed(1);
        const firstX = (pts[0].xPct * 8).toFixed(1);
        return `${firstX},230 ${lineCoords} ${lastX},230`;
    });

    /** Y position % for Capacity Limit line */
    readonly capacityYPosPct = computed(() => {
        const cap = this.capacity();
        const max = this.yAxisMax();
        return Math.max(5, Math.min(95, 100 - (cap / max) * 100));
    });

    /** Overbooked periods with exact plates tracking */
    readonly overbookedPeriods = computed<OverbookedPeriodIncident[]>(() => {
        const pts = this.stepPoints();
        const cap = this.capacity();
        const spanMs = this.timeSpanMs();
        const startMs = this.effectiveStart();
        const evs = this.events();
        const incidents: OverbookedPeriodIncident[] = [];

        let currentIncidentStart: OccupancyStepPoint | null = null;
        let peak = 0;

        const buildIncidentPlates = (start: Date, end: Date) => {
            const startT = start.getTime();
            const endT = end.getTime();
            const activeInIncident = evs.filter(e => {
                const s = new Date(e.startedAt).getTime();
                const ed = e.endedAt ? new Date(e.endedAt).getTime() : Infinity;
                return s < endT && ed > startT;
            }).sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());

            const activeSet = new Set<string>();
            const coveredSet = new Set<string>();
            const overflowSet = new Set<string>();

            for (const e of activeInIncident) {
                const p = e.plate || 'Unknown Plate';
                activeSet.add(p);
                if (e.isOverflow) {
                    overflowSet.add(p);
                    coveredSet.delete(p);
                } else if (coveredSet.size < cap && !overflowSet.has(p)) {
                    coveredSet.add(p);
                } else if (!coveredSet.has(p)) {
                    overflowSet.add(p);
                }
            }

            return {
                activePlates: Array.from(activeSet),
                overflowPlates: Array.from(overflowSet),
                coveredPlates: Array.from(coveredSet)
            };
        };

        for (let i = 0; i < pts.length; i++) {
            const p = pts[i];
            if (p.occupancy > cap) {
                if (!currentIncidentStart) {
                    currentIncidentStart = p;
                    peak = p.occupancy;
                } else {
                    peak = Math.max(peak, p.occupancy);
                }
            } else if (currentIncidentStart) {
                const durationMinutes = Math.round((p.timeMs - currentIncidentStart.timeMs) / (1000 * 60));
                const xStartPct = Math.max(0, ((currentIncidentStart.timeMs - startMs) / spanMs) * 100);
                const xEndPct = Math.min(100, ((p.timeMs - startMs) / spanMs) * 100);
                const plates = buildIncidentPlates(currentIncidentStart.time, p.time);

                incidents.push({
                    startTime: currentIncidentStart.time,
                    endTime: p.time,
                    isOngoing: false,
                    durationMinutes: Math.max(1, durationMinutes),
                    peakOccupancy: peak,
                    excessCars: peak - cap,
                    xStartPct,
                    xWidthPct: Math.max(1, xEndPct - xStartPct),
                    activePlates: plates.activePlates,
                    overflowPlates: plates.overflowPlates,
                    coveredPlates: plates.coveredPlates
                });
                currentIncidentStart = null;
            }
        }

        // Open incident still overbooked at end of timeframe
        if (currentIncidentStart) {
            const end = pts[pts.length - 1];
            const durationMinutes = Math.round((end.timeMs - currentIncidentStart.timeMs) / (1000 * 60));
            const xStartPct = Math.max(0, ((currentIncidentStart.timeMs - startMs) / spanMs) * 100);
            const xEndPct = 100;
            const plates = buildIncidentPlates(currentIncidentStart.time, end.time);

            incidents.push({
                startTime: currentIncidentStart.time,
                endTime: end.time,
                isOngoing: true,
                durationMinutes: Math.max(1, durationMinutes),
                peakOccupancy: peak,
                excessCars: peak - cap,
                xStartPct,
                xWidthPct: Math.max(1, xEndPct - xStartPct),
                activePlates: plates.activePlates,
                overflowPlates: plates.overflowPlates,
                coveredPlates: plates.coveredPlates
            });
        }

        return incidents;
    });

    /** Distinct list of all overbooked vehicles & plates */
    readonly overbookedVehiclesSummary = computed<OverbookedVehicleSummary[]>(() => {
        const evs = this.events();
        const cap = this.capacity();
        const result: OverbookedVehicleSummary[] = [];
        const seenPlates = new Set<string>();

        // Gather explicit overflow stays OR vehicles present in overbooked periods
        const incidents = this.overbookedPeriods();
        const overflowPlateNames = new Set<string>();
        incidents.forEach(inc => inc.overflowPlates.forEach(p => overflowPlateNames.add(p)));

        for (const e of evs) {
            const plate = e.plate;
            if (!plate) continue;

            const isOverflow = e.isOverflow || overflowPlateNames.has(plate);
            if (isOverflow && !seenPlates.has(plate)) {
                seenPlates.add(plate);
                const s = new Date(e.startedAt);
                const end = e.endedAt ? new Date(e.endedAt) : null;
                const endMs = end ? end.getTime() : Date.now();
                const durationMinutes = Math.max(1, Math.round((endMs - s.getTime()) / (1000 * 60)));

                result.push({
                    plate,
                    startedAt: s,
                    endedAt: end,
                    isCurrentlyActive: !e.endedAt,
                    durationMinutes
                });
            }
        }

        return result;
    });

    /** Aggregates & KPIs */
    readonly totalStaysCount = computed(() => this.events().length);

    readonly peakOccupancy = computed(() => {
        const pts = this.stepPoints();
        return pts.reduce((max, p) => Math.max(max, p.occupancy), 0);
    });

    readonly totalTimesOverbooked = computed(() => this.overbookedPeriods().length);

    readonly totalMinutesOverbooked = computed(() => {
        return this.overbookedPeriods().reduce((sum, f) => sum + f.durationMinutes, 0);
    });

    readonly formattedTotalTimeOverbooked = computed(() => {
        const mins = this.totalMinutesOverbooked();
        if (mins === 0) return '0 min';
        const hours = Math.floor(mins / 60);
        const remMins = mins % 60;
        return hours > 0 ? `${hours}h ${remMins}m` : `${remMins}m`;
    });

    readonly overflowEventsCount = computed(() => {
        return this.events().filter(e => e.isOverflow).length;
    });

    /** Time marks on X-axis with clean date and time */
    readonly timeAxisMarks = computed<{ label: string; timeLabel: string; dateLabel: string; xPct: number; align: 'left' | 'center' | 'right' }[]>(() => {
        const start = this.effectiveStart();
        const end = this.effectiveEnd();
        const spanMs = end - start;
        const marks: { label: string; timeLabel: string; dateLabel: string; xPct: number; align: 'left' | 'center' | 'right' }[] = [];
        const count = 5;
        const isMultiDay = spanMs > 48 * 3600 * 1000;

        for (let i = 0; i <= count; i++) {
            const tMs = start + (i / count) * spanMs;
            const d = new Date(tMs);

            let timeLabel: string;
            let dateLabel: string;

            if (isMultiDay) {
                timeLabel = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                dateLabel = d.toLocaleDateString([], { weekday: 'short' });
            } else {
                timeLabel = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                dateLabel = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            }

            let align: 'left' | 'center' | 'right' = 'center';
            if (i === 0) align = 'left';
            else if (i === count) align = 'right';

            marks.push({
                label: isMultiDay ? `${timeLabel} (${dateLabel})` : `${dateLabel}, ${timeLabel}`,
                timeLabel,
                dateLabel,
                xPct: (i / count) * 100,
                align
            });
        }

        return marks;
    });

    onChartMouseMove(event: MouseEvent): void {
        const target = event.currentTarget as HTMLElement;
        if (!target) return;

        const rect = target.getBoundingClientRect();
        if (rect.width <= 0) return;

        const clientX = event.clientX;
        const relativeX = Math.max(0, Math.min(rect.width, clientX - rect.left));
        const xPct = (relativeX / rect.width) * 100;
        this.hoverX.set(xPct);

        const startMs = this.effectiveStart();
        const spanMs = this.timeSpanMs();
        const cursorTimeMs = startMs + (xPct / 100) * spanMs;
        const cursorDate = new Date(cursorTimeMs);
        const cap = this.capacity();
        const evs = this.events();

        // Find all events active at the exact cursorTimeMs (ongoing stays have no endedAt, so they continue indefinitely)
        const activeEvents = evs.filter(e => {
            const s = new Date(e.startedAt).getTime();
            const ed = e.endedAt ? new Date(e.endedAt).getTime() : Infinity;
            return s <= cursorTimeMs && ed > cursorTimeMs;
        }).sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());

        const activeSet = new Set<string>();
        const coveredSet = new Set<string>();
        const overflowSet = new Set<string>();

        for (const e of activeEvents) {
            const p = e.plate || 'Unknown Plate';
            activeSet.add(p);
            if (e.isOverflow) {
                overflowSet.add(p);
                coveredSet.delete(p);
            } else if (coveredSet.size < cap && !overflowSet.has(p)) {
                coveredSet.add(p);
            } else if (!coveredSet.has(p)) {
                overflowSet.add(p);
            }
        }

        const occupancy = activeSet.size;
        const activePlates = Array.from(activeSet);
        const overflowPlates = Array.from(overflowSet);
        const coveredPlates = Array.from(coveredSet);

        const yMax = this.yAxisMax();
        const yPct = Math.max(0, 100 - (occupancy / yMax) * 100);
        const peak = this.peakOccupancy();

        this.hoveredPoint.set({
            time: cursorDate,
            timeMs: cursorTimeMs,
            occupancy,
            activePlates,
            overflowPlates,
            coveredPlates,
            isAtCapacity: occupancy === cap && occupancy > 0,
            isOverbooked: occupancy > cap,
            isPeak: occupancy > 0 && occupancy === peak,
            hasOverflow: occupancy > cap,
            xPct,
            yPct
        });
    }

    onChartMouseLeave(): void {
        this.hoveredPoint.set(null);
        this.hoverX.set(null);
    }

    setPreset(preset: 'fit' | '24h' | '7d' | 'all'): void {
        this.viewPreset.set(preset);
    }

    formatTime(date: Date): string {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    formatDuration(minutes: number): string {
        if (!minutes || minutes <= 0) return '0 min';
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const rem = minutes % 60;
        return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
    }
}
